'use client'

import * as React from 'react'
import { cn } from '../../utils'

export interface EntityListShellProps {
  /** Page title. Rendered above the toolbar. */
  title?: React.ReactNode
  /** Page subtitle / hint. */
  description?: React.ReactNode
  /** Toolbar content — usually a search input + filters. */
  toolbar?: React.ReactNode
  /** Right-aligned actions in the toolbar (e.g. "Create" button). */
  actions?: React.ReactNode
  /** The list/table itself. */
  children?: React.ReactNode
  /** Pagination control. Rendered under the body. */
  pagination?: React.ReactNode
  /** When true, body shows a centered empty state instead of children. */
  empty?: boolean
  emptyState?: React.ReactNode
  /** When true, a translucent overlay covers the body (live refresh). */
  loading?: boolean
  className?: string
}

/**
 * Standard wrapper for every admin list view: header (title + actions),
 * toolbar (search + filters), body (table or list), and pagination.
 *
 * Built so each list page reduces to:
 *   <EntityListShell title="Quotes" toolbar={<SearchInput />} actions={<Button>Create</Button>} pagination={<Pagination …/>}>
 *     <Table>…</Table>
 *   </EntityListShell>
 */
export function EntityListShell({
  title,
  description,
  toolbar,
  actions,
  children,
  pagination,
  empty,
  emptyState,
  loading,
  className,
}: EntityListShellProps) {
  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {(title || description || actions) && (
        <header className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {title && (
              <h1 className="text-h1 font-semibold truncate">{title}</h1>
            )}
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-2 shrink-0">{actions}</div>
          )}
        </header>
      )}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {toolbar && (
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            {toolbar}
          </div>
        )}

        <div className="relative">
          {empty ? (
            <div className="px-6 py-16 text-center text-sm text-muted-foreground">
              {emptyState ?? 'No items found.'}
            </div>
          ) : (
            children
          )}

          {loading && (
            <div
              aria-hidden
              className="absolute inset-0 bg-background/40 backdrop-blur-[1px] pointer-events-none"
            />
          )}
        </div>

        {pagination && (
          <div className="border-t border-border">{pagination}</div>
        )}
      </div>
    </div>
  )
}
